import React from 'react';
import { Check } from 'lucide-react';
import { TestType } from '@/models/SharedTypes';
import {
  ATTEMPT_OPTIONS,
  BLUEPRINT_CHANGE_LABEL,
  contentAreasForExam,
  type RetakeAnswers,
} from '@/features/onboarding/retakeReport';
import type { RetakeBlueprint } from '@/models/UserTypes';

interface RetakeStepProps {
  exam: TestType;
  value: RetakeAnswers;
  onChange: (next: RetakeAnswers) => void;
}

/**
 * Onboarding step: "Have you taken the {exam} before?"
 *
 * Optional by construction. "No" is preselected, so a visitor who reads nothing
 * and presses Continue writes NOTHING (buildRetakeReport returns null). Only a
 * "Yes" produces a retakeReport.
 *
 * Visual anatomy is the same as the targetExam step (.pen "Onboarding 2 — Exam
 * Type"): surface cards on the cream ground, brand-tint fill + brand check disc
 * for the selected state, hard ledge on the primary action. No gradients.
 */
const RetakeStep: React.FC<RetakeStepProps> = ({ exam, value, onChange }) => {
  const areas = contentAreasForExam(exam, value.blueprint);
  const set = (patch: Partial<RetakeAnswers>) => onChange({ ...value, ...patch });

  // Switching eras changes WHICH areas exist, so any selection that does not
  // survive the switch is dropped here rather than lingering invisibly in state
  // and reappearing if the user switches back.
  const setBlueprint = (blueprint: RetakeBlueprint) => {
    const valid = new Set(contentAreasForExam(exam, blueprint).map((a) => a.code as string));
    set({ blueprint, lowestAreas: value.lowestAreas.filter((c) => valid.has(c)) });
  };

  const toggleArea = (code: string) =>
    set({
      lowestAreas: value.lowestAreas.includes(code)
        ? value.lowestAreas.filter((c) => c !== code)
        : [...value.lowestAreas, code],
    });

  return (
    <div className="space-y-8">
      <div>
        <h1
          className="text-3xl sm:text-4xl font-semibold text-neutral-900 mb-3"
          style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}
        >
          Have you taken the {exam.toUpperCase()} before?
        </h1>
        <p className="text-lg text-neutral-600">
          I ask because it changes your first session. If you have sat for this exam, I start you
          where it hurt instead of at question one.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {[
          { taken: false, title: 'No, this is my first time', sub: 'We start fresh' },
          { taken: true, title: 'Yes, I have taken it', sub: 'We start with your score report' },
        ].map((option) => {
          const isSelected = value.hasRetaken === option.taken;
          return (
            <button
              key={option.title}
              type="button"
              aria-pressed={isSelected}
              onClick={() => set({ hasRetaken: option.taken })}
              className={`p-5 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 bg-neutral-100 hover:border-neutral-300'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`font-semibold ${isSelected ? 'text-primary-700' : 'text-neutral-900'}`}
                >
                  {option.title}
                </span>
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <span className="text-sm text-neutral-500">{option.sub}</span>
            </button>
          );
        })}
      </div>

      {value.hasRetaken && (
        <div className="space-y-8 pt-2 border-t border-neutral-200">
          <fieldset className="space-y-3 pt-6">
            <legend className="text-sm font-medium text-neutral-700 mb-3">
              When did you last take it?
            </legend>
            <p className="text-sm text-neutral-500 mb-3">
              ASWB reorganized the exam on {BLUEPRINT_CHANGE_LABEL}. Older score reports list four
              content areas, newer ones list three, so I want to read yours the right way.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {([
                { era: 'aug2026' as const, label: `On or after ${BLUEPRINT_CHANGE_LABEL}`, sub: 'Three content areas' },
                { era: 'pre2026' as const, label: `Before ${BLUEPRINT_CHANGE_LABEL}`, sub: 'Four content areas' },
              ]).map((option) => {
                const isSelected = value.blueprint === option.era;
                return (
                  <button
                    key={option.era}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setBlueprint(option.era)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 bg-neutral-100 hover:border-neutral-300'
                    }`}
                  >
                    <span
                      className={`block font-semibold ${
                        isSelected ? 'text-primary-700' : 'text-neutral-900'
                      }`}
                    >
                      {option.label}
                    </span>
                    <span className="block text-sm text-neutral-500">{option.sub}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-neutral-700 mb-3">How many times?</legend>
            <div className="grid grid-cols-3 gap-3">
              {ATTEMPT_OPTIONS.map((n) => {
                const isSelected = value.attempts === n;
                return (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => set({ attempts: n })}
                    className={`py-3 rounded-xl border-2 font-semibold transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 bg-neutral-100 text-neutral-900 hover:border-neutral-300'
                    }`}
                  >
                    {n === 3 ? '3+' : n}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-neutral-700">
              Which areas were lowest on your score report?
            </legend>
            <p className="text-sm text-neutral-500">
              Pick any that apply. Skip this if you do not have the report in front of you.
            </p>
            <div className="grid gap-3">
              {areas.map((area) => {
                const isSelected = value.lowestAreas.includes(area.code);
                return (
                  <button
                    key={area.code}
                    type="button"
                    role="checkbox"
                    aria-checked={isSelected}
                    onClick={() => toggleArea(area.code)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 bg-neutral-100 hover:border-neutral-300'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-primary-500' : 'border-2 border-neutral-300'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span
                      className={`font-medium ${
                        isSelected ? 'text-primary-700' : 'text-neutral-900'
                      }`}
                    >
                      {area.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-neutral-200 bg-neutral-100 cursor-pointer">
            <input
              type="checkbox"
              checked={value.nearMiss}
              onChange={(e) => set({ nearMiss: e.target.checked })}
              className="mt-1 w-5 h-5 accent-primary-500"
            />
            <span>
              <span className="block font-medium text-neutral-900">
                I was within 10 correct of passing
              </span>
              <span className="block text-sm text-neutral-500">
                It tells me how close your recall already is.
              </span>
            </span>
          </label>

          {/* Deliberately NOT conditioned on the near-miss box: the wait is set by
              the state board, not by how close you came, and tying the note to the
              checkbox implied the box was what shortened it. */}
          <p className="text-sm text-neutral-500">
            Retake waits vary by state. Ask your board before you assume 90 days.
          </p>
        </div>
      )}
    </div>
  );
};

export default RetakeStep;
